export type MountainAIWeatherCoordinate = {
  latitude: number;
  longitude: number;
  elevation: number | null;
};

export type MountainAIWeatherRouteGeoJson = {
  type: "FeatureCollection";
  features: Array<{
    type: "Feature";
    properties: Record<string, string>;
    geometry:
      | { type: "LineString"; coordinates: number[][] }
      | { type: "Point"; coordinates: number[] };
  }>;
};

export type MountainAIWeatherTarget =
  | { key: string; kind: "route"; routeId: string; geojson: MountainAIWeatherRouteGeoJson }
  | { key: string; kind: "point"; routeId: string; point: MountainAIWeatherCoordinate }
  | null;

type WeatherTargetInput = {
  recordingMode: "selected-route" | "free" | null;
  selectedRouteId: string;
  recordingGeojson?: MountainAIWeatherRouteGeoJson;
  routeChosen: boolean;
  selectedRouteGeojson?: MountainAIWeatherRouteGeoJson;
  currentPosition?: MountainAIWeatherCoordinate | null;
  selectedPlace?: (MountainAIWeatherCoordinate & { id: string }) | null;
};

function validRoute(geojson?: MountainAIWeatherRouteGeoJson) {
  return Boolean(geojson?.features.some(
    (feature) => feature.geometry.type === "LineString" && feature.geometry.coordinates.length >= 2,
  ));
}

function quantizedPoint(point: MountainAIWeatherCoordinate): MountainAIWeatherCoordinate {
  // El clima no necesita seguir cada lectura GPS. Redondear a ~1 km evita
  // consultas repetidas mientras el usuario camina y conserva una cache local util.
  return {
    latitude: Number(point.latitude.toFixed(2)),
    longitude: Number(point.longitude.toFixed(2)),
    elevation: point.elevation,
  };
}

export function selectMountainAIWeatherTarget(input: WeatherTargetInput): MountainAIWeatherTarget {
  if (input.recordingMode === "selected-route" && validRoute(input.recordingGeojson)) {
    return {
      key: `route:${input.selectedRouteId}`,
      kind: "route",
      routeId: `mountain-ai-route-${input.selectedRouteId}`,
      geojson: input.recordingGeojson!,
    };
  }

  if (input.currentPosition) {
    const point = quantizedPoint(input.currentPosition);
    return {
      key: `point:${point.latitude.toFixed(2)}:${point.longitude.toFixed(2)}`,
      kind: "point",
      routeId: `mountain-ai-gps-${point.latitude.toFixed(2)}-${point.longitude.toFixed(2)}`,
      point,
    };
  }

  if (input.recordingMode === "free") return null;

  if (input.routeChosen && validRoute(input.selectedRouteGeojson)) {
    return {
      key: `route:${input.selectedRouteId}`,
      kind: "route",
      routeId: `mountain-ai-route-${input.selectedRouteId}`,
      geojson: input.selectedRouteGeojson!,
    };
  }

  if (input.selectedPlace) {
    const point = quantizedPoint(input.selectedPlace);
    return {
      key: `place:${input.selectedPlace.id}`,
      kind: "point",
      routeId: `mountain-ai-place-${input.selectedPlace.id}`,
      point,
    };
  }

  return null;
}
